import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { I18nProvider } from '@lingui/react';
// msg, not t: App renders the I18nProvider, so useLingui() has no context here.
import { msg } from '@lingui/core/macro';
import { AuthContext } from './contexts/AuthContext';
import { TierProvider } from './contexts/TierContext';
import { i18n, activateLocale, DEFAULT_LOCALE } from './i18n';
import { fetchAccountLocale } from './services/localeService';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { authService } from './services/authService';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import SubscriptionPage from './pages/SubscriptionPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import SalesforceOAuthCallbackPage from './pages/SalesforceOAuthCallbackPage';
import AgentsPage from './pages/AgentsPage';
import InvitePage from './pages/InvitePage';
import SignupPage from './pages/SignupPage';
import OnboardingPage from './pages/OnboardingPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import TierGate from './components/TierGate';
import UserManagementPage from './pages/UserManagementPage';
import UserDetailAuditPage from './pages/UserDetailAuditPage';
import ApiKeysPage from './pages/ApiKeysPage';
import ConnectionsPage from './pages/ConnectionsPage';
import PrivateMcpPage from './pages/PrivateMcpPage';
import MemoryPage from './pages/MemoryPage';
import './App.css';

function App() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // The account is authoritative for the interface language, the same way it is
  // for the desktop app: locale is a per-user setting resolved server side, so
  // the portal reads it rather than guessing from the browser. An absent or
  // unknown value falls to en-US, which is the source locale and always renders.
  useEffect(() => {
    // Activate immediately from whatever the cached user carries, so a reload
    // does not flash English before the fetch lands.
    activateLocale(user?.localePreference ?? user?.locale ?? DEFAULT_LOCALE);
    if (!user) return;
    // Then confirm against the account itself. The login response carries no
    // locale, so the cached user's value is absent for every existing session;
    // this endpoint is the same one the desktop reconciles against, and it is
    // the authority. A failure returns null and leaves the value above in place.
    let cancelled = false;
    fetchAccountLocale().then((tag) => {
      if (!cancelled && tag) activateLocale(tag);
    });
    return () => { cancelled = true; };
  }, [user, user?.localePreference, user?.locale]);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Restore session from token passed by proxy (e.g. /signup#pp_token=xxx)
        const hash = window.location.hash;
        const tokenMatch = hash.match(/[?&]pp_token=([^&]+)/) || hash.match(/^#?pp_token=([^&]+)/);
        if (tokenMatch && tokenMatch[1]) {
          const token = decodeURIComponent(tokenMatch[1]);
          if (authService.restoreFromToken(token)) {
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
          }
        }
        const currentUser = await authService.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
        }
      } catch (error) {
        console.error('Auth init error:', error);
      } finally {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    const result = await authService.login(email, password);
    if (result.success) {
      setUser(result.user);
    }
    return result;
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
  };

  const ssoLogin = async (provider: 'google' | 'microsoft', inviteHash?: string) => {
    return await authService.ssoLogin(provider, inviteHash);
  };

  const refreshUser = async () => {
    const currentUser = await authService.getCurrentUser();
    setUser(currentUser);
  };

  if (loading) {
    return (
      <div className="app">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <I18nProvider i18n={i18n}>
    <ThemeProvider>
    <ToastProvider>
      <AuthContext.Provider value={{ user, login, logout, ssoLogin, refreshUser, setUser }}>
        <TierProvider>
        <BrowserRouter>
          <Routes>
            <Route 
              path="/login" 
              element={user ? <Navigate to="/signup" /> : <LoginPage />} 
            />
            <Route 
              path="/dashboard" 
              element={<ProtectedRoute user={user}><DashboardPage /></ProtectedRoute>} 
            />
            <Route 
              path="/subscription" 
              element={<ProtectedRoute user={user}><SubscriptionPage /></ProtectedRoute>} 
            />
            <Route
              path="/agents"
              element={
                <ProtectedRoute user={user}>
                  <TierGate
                    feature={msg`Privacy Agents`}
                    description={msg`Intelligent agents that watch your privacy pipeline in real time and analyze audit data for anomalies.`}
                  >
                    <AgentsPage />
                  </TierGate>
                </ProtectedRoute>
              }
            />
            <Route 
              path="/auth/callback" 
              element={<AuthCallbackPage />} 
            />
            <Route
              path="/connections/oauth/callback"
              element={<SalesforceOAuthCallbackPage />}
            />
            <Route 
              path="/invite/:hash" 
              element={<InvitePage />} 
            />
            <Route
              path="/signup"
              element={<SignupPage />}
            />
            <Route
              path="/verify-email"
              element={<VerifyEmailPage />}
            />
            <Route
              path="/forgot-password"
              element={<ForgotPasswordPage />}
            />
            <Route
              path="/reset-password"
              element={<ResetPasswordPage />}
            />
            <Route
              path="/api-keys"
              element={
                <ProtectedRoute user={user}>
                  <TierGate
                    feature={msg`API Access`}
                    description={msg`Developer keys for the PrivacyPal API and SDK: encode, decode and batch protection inside your own tools and pipelines.`}
                  >
                    <ApiKeysPage />
                  </TierGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/connections"
              element={
                <ProtectedRoute user={user}>
                  <TierGate
                    feature={msg`IP Connections`}
                    description={msg`Connect Salesforce, HubSpot, Notion, and other business systems so your AI works on Privacy Twins, never the real records.`}
                  >
                    <ConnectionsPage />
                  </TierGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/private-mcp"
              element={
                <ProtectedRoute user={user}>
                  <TierGate
                    feature={msg`Private MCP`}
                    description={msg`A hosted MCP endpoint that lets your AI assistant use Salesforce, HubSpot, Microsoft 365 and Google Workspace while it only ever sees Privacy Twins.`}
                  >
                    <PrivateMcpPage />
                  </TierGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/memory"
              element={<ProtectedRoute user={user}><MemoryPage /></ProtectedRoute>}
            />
            <Route 
              path="/users" 
              element={<ProtectedRoute user={user}><UserManagementPage /></ProtectedRoute>} 
            />
            <Route
              path="/users/detail/:userId"
              element={<ProtectedRoute user={user}><UserDetailAuditPage /></ProtectedRoute>}
            />
            <Route
              path="/onboarding"
              element={user ? <OnboardingPage /> : <Navigate to="/login" replace />}
            />
            <Route path="/" element={<Navigate to={user ? "/signup" : "/login"} />} />
          </Routes>
        </BrowserRouter>
        </TierProvider>
      </AuthContext.Provider>
    </ToastProvider>
    </ThemeProvider>
    </I18nProvider>
  );
}

export default App;
